import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/common/BackButton";
import CustomiseSection from "../../components/collection/CustomiseSection";

// 1. The Individual Product Card Component
const EmbroideryCard = ({ product, onViewClick }) => {
    const [loupePos, setLoupePos] = useState({ x: 0, y: 0 });
    const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
    const imageWrapperRef = useRef(null);

    // This function calculates the mouse position relative to the image container
    // and updates the position of the loupe circle and the image inside it.
    const handleMouseMove = (e) => {
        const { left, top, width, height } =
            imageWrapperRef.current.getBoundingClientRect();

        // Mouse coordinates relative to the wrapper
        const x = e.clientX - left;
        const y = e.clientY - top;

        // Update Loupe Position (centered on mouse)
        setLoupePos({ x, y });

        // Update magnified image position *inside* the loupe.
        // New Formula:
        // We want the point (x, y) on the small image to correspond to the center of the loupe.
        // The large image is at -x * scale, -y * scale relative to the origin.
        // We add loupeRadius to center that 'view' in the loupe div.

        const scale = 2.8; // Matches CSS width: 250%
        const loupeRadius = 90; // Matches CSS width/height: 180px / 2 = 90

        setImagePos({
            x: -(x * scale) + loupeRadius,
            y: -(y * scale) + loupeRadius,
        });
    };

    return (
        <div className="pe-card-wrapper">
            <div className="pe-card pe-static-card">
                {/* --- Image Wrapper with Mouse Tracking --- */}
                <div
                    className="pe-image-wrapper"
                    ref={imageWrapperRef}
                    onMouseMove={handleMouseMove}
                // On mouse leave, maybe reset? Optional, but feels smoother if left as is.
                >
                    {/* The "Base" Photo (Flat) */}
                    <img
                        src={product.imageBase}
                        alt={product.title}
                        className="pe-base-image"
                    />

                    {/* The Magnifying Loupe Circle */}
                    <div
                        className="pe-loupe"
                        style={{ top: `${loupePos.y}px`, left: `${loupePos.x}px` }}
                    >
                        {/* The "Textured" Embroidery Image inside the loupe */}
                        <img
                            src={product.imageTexture}
                            alt="Embroidery Detail"
                            className="pe-loupe-image"
                            style={{
                                transform: `translate(${imagePos.x}px, ${imagePos.y}px)`,
                            }}
                        />
                    </div>
                </div>

                {/* --- Card Details --- */}
                <div className="pe-details">
                    {/* Replaced Category with ID */}
                    <span className="pe-category">PRODUCT ID: {product.id}</span>
                    <h3 className="pe-title">{product.title}</h3>

                    <div className="pe-button-container">
                        <button className="pe-btn" onClick={() => onViewClick(product)}>Click to View</button>
                    </div>
                </div>
            </div>
        </div>
    );
};




// 2. Main Container displaying the grid
export default function Keychains() {
    const navigate = useNavigate();

    // DUMMY DATA - Replace with your real data
    // IMPT: Use real, high-res embroidery closeups for 'imageTexture'
    const products = [
        {
            id: 'KC-01',
            category: "Crochet Keychains",
            title: "Sunflower Charm",
            price: "₹ 179",
            imageBase: "images/carousel-3.webp",
            imageTexture: "images/carousel-3.webp",
            description: "A cheerful sunflower crochet keychain handcrafted to add warmth and positivity to your everyday essentials.",
            highlights: ["Hand-crocheted flower", "Durable yarn finish", "Lightweight & charming"]
        },
        {
            id: 'KC-02',
            category: "Initial Accessories",
            title: "Letter Key Charm",
            price: "₹ 179",
            imageBase: "images/key1.webp",
            imageTexture: "images/key1.webp",
            description: "A bold letter-shaped crochet accessory designed to personalize your bags, keys, or gifts.",
            highlights: ["Custom initial option", "Strong crochet build", "Perfect gifting accessory"]
        },
        {
            id: 'KC-03',
            category: "Personalized Keychains",
            title: "Name Thread Tag",
            price: "₹ 179",
            imageBase: "images/key2.webp",
            imageTexture: "images/key2.webp",
            description: "A minimal embroidered name keychain crafted for everyday use. Simple, meaningful, and personal.",
            highlights: ["Custom name embroidery", "Clean fabric finish", "Ideal for keys & bags"]
        },
        {
            id: 'KC-04',
            category: "Evil Eye Accessories",
            title: "Protection Charm",
            price: "₹ 179",
            imageBase: "images/key3.webp",
            imageTexture: "images/key3.webp",
            description: "A handcrafted evil eye crochet keychain designed to bring positivity and protection wherever you go.",
            highlights: ["Symbolic evil-eye design", "Handmade crochet", "Durable ring attachment"]
        },
        {
            id: 'KC-05',
            category: "Fruit Crochet Charms",
            title: "Cherry Pair",
            price: "₹ 179",
            imageBase: "images/key4.webp",
            imageTexture: "images/key4.webp",
            description: "Playful cherry-shaped crochet charms crafted with vibrant yarns. A fun pop of color for your accessories.",
            highlights: ["Hand-crocheted fruit design", "Soft yet sturdy threads", "Youthful & playful"]
        },
        {
            id: 'KC-06',
            category: "Nature-Inspired Charms",
            title: "Berry Bloom",
            price: "₹ 179",
            imageBase: "images/key5.webp",
            imageTexture: "images/key5.webp",
            description: "A delicate berry-inspired crochet charm that blends natural forms with handcrafted beauty.",
            highlights: ["Nature-inspired crochet", "Premium yarn finish", "Elegant everyday accessory"]
        },
        {
            id: 'KC-07',
            category: "Kids Crochet Accessories",
            title: "Smiley Bloom",
            price: "₹ 199",
            imageBase: "images/key6.webp",
            imageTexture: "images/key6.webp",
            description: "A cute smiling flower crochet charm designed especially for kids, spreading joy with every stitch.",
            highlights: ["Kid-friendly design", "Soft crochet texture", "Safe & lightweight"]
        },
        {
            id: 'KC-08',
            category: "Animal Crochet Charms",
            title: "Little Bee Keycharm",
            price: "₹ 249",
            imageBase: "images/key7.webp",
            imageTexture: "images/key7.webp",
            description: "An adorable bee-shaped crochet charm, perfect for little ones and animal lovers alike.",
            highlights: ["Cute bee design", "Soft crochet yarn", "Playful & lightweight"]
        },
        {
            id: 'KC-09',
            category: "Whimsical Accessories",
            title: "Strawberry Keycharm",
            price: "₹ 199",
            imageBase: "images/key8.webp",
            imageTexture: "images/key8.webp",
            description: "A bright and whimsical strawberry crochet keychain that brings a touch of magic to everyday items.",
            highlights: ["Whimsical strawberry shape", "Vibrant colors", "Sturdy & fun"]
        },
        {
            id: 'KC-10',
            category: "Floral Mini Charms",
            title: "Mini Bow Charm",
            price: "₹ 199",
            imageBase: "images/key9.webp",
            imageTexture: "images/key9.webp",
            description: "A delicate and charming mini bow crochet keychain, beautifully handcrafted for a sweet look.",
            highlights: ["Miniature bow design", "Detailed hand-crochet", "Elegant accessory"]
        },
        {
            id: 'KC-11',
            category: "Heart Shaped Mini Charms",
            title: "Heart Shaped Keycharm",
            price: "₹ 199",
            imageBase: "images/key10.webp",
            imageTexture: "images/key10.webp",
            description: "A lovable and charming heart shaped crochet keychain, beautifully handcrafted for a sweet look.",
            highlights: ["Heart shape design", "Detailed hand-crochet", "Elegant accessory"]
        },
    ];



    const handleViewProduct = (product) => {
        navigate('/product-view', { state: { product } });
    };

    return (
        <>
            <section className="pe-section">
                <div style={{ padding: "0 5%", maxWidth: "1200px", margin: "0 auto", marginTop: "20px" }}>
                    <BackButton />
                </div>
                <div className="pe-header">
                    <h2 className="pe-main-title">❖ Keycharms Collection ❖</h2>
                    <div className="pe-thread-line"></div>
                    <p className="pe-subtitle">
                        Turn simple keys into something special with handmade key charms, crafted to reflect personality and warmth.
                    </p>
                </div>

                <div className="pe-grid">
                    {products.map((product) => (
                        <EmbroideryCard key={product.id} product={product} onViewClick={handleViewProduct} />
                    ))}

                </div>
            </section>
            <CustomiseSection />
        </>
    );
}

